import Player, { type Options } from '@vimeo/player';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { CoreCallToActionData } from 'src/types/components';

import Image from 'components/BaseHelpers/Image';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';
import TextCTA from 'components/BaseHelpers/TextCTA';

import styles from './CoreCallToAction.module.scss';

const urlRegex = /https:\/\/player\.vimeo\.com\/video\/\d+/;

type RenderVideoProps = { id: string };
const RenderVideo = ({ id }: RenderVideoProps) => {
  const videoRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const videoPlayer = useRef<null | Player>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!videoRef.current) return;

    const useURLInstead = urlRegex.test(id);

    const options: Options = {
      ...(useURLInstead ? { url: id } : { id: parseInt(id) }),
    };

    const player = new Player(videoRef.current, options);

    player.on('error', (e) => {
      console.error(e);
    });

    player.on('loaded', () => {
      setReady(true);
    });

    videoPlayer.current = player;
  }, [id, videoRef]);

  const playVideo = () => {
    if (!videoPlayer.current) {
      return;
    }
    setPlaying(true);
    videoPlayer.current.play();
  };

  return (
    <div
      className={cn(styles.videoContainer, {
        [styles['videoContainer--ready']]: ready,
        [styles['videoContainer--playing']]: playing,
      })}
    >
      <div
        data-vimeo-fullscreen="false"
        data-vimeo-vimeo_logo="false"
        ref={videoRef}
        className={cn(styles.video)}
      ></div>
      <button onClick={playVideo} className={styles.playButton} aria-label="Play video">
        <span className={styles.playIcon} />
      </button>
    </div>
  );
};

type RenderMediaProps = {
  media: CoreCallToActionData['fields']['media'];
};
const RenderMedia = ({ media }: RenderMediaProps) => {
  if (!media || !media.image) {
    return null;
  }
  const { image, video } = media;
  return (
    <div className={styles.innerImageWrapper}>
      <Image className={styles.image} {...image} fill />
      {!!video && (
        <>
          <RenderVideo id={video} />
        </>
      )}
    </div>
  );
};

export type CoreCallToActionProps = CoreCallToActionData;

const CoreCallToAction = ({ fields }: CoreCallToActionProps): JSX.Element | null => {
  const {
    header,
    mediaFormat,
    theme,
    media,
    primaryCallToAction,
    secondaryCallToAction,
    anchorTag,
  } = fields;
  const { eyebrow, heading, description } = header;

  const containerClasses = cn(
    {
      container: mediaFormat === 'none',
      'container--true-1440': mediaFormat === 'none',
      'container-large': mediaFormat !== 'none',
    },
    styles.globalContainer
  );

  const outerWrapperClasses = cn(
    styles.outerWrapper,
    styles[`media--${mediaFormat}`],
    styles[`theme--${theme}`],
    'spacer',
    'spacer--solid'
  );

  return (
    <section id={anchorTag} className={outerWrapperClasses}>
      <div className={cn(styles.desktopContext)}>
        <div className={containerClasses}>
          <div className={styles.innerWrapper}>
            <div className={styles.imageWrapper}>
              <RenderMedia media={media} />
            </div>
            <div className={styles.copyWrapper}>
              {!!eyebrow && (
                <p className={styles.eyebrow}>
                  <Text field={eyebrow} />
                </p>
              )}
              <h2 className={styles.heading}>
                <Text field={heading} />
              </h2>
              {!!description && <RichText className={styles.description} field={description} />}
              {(!!primaryCallToAction?.href || !!secondaryCallToAction?.href) && (
                <ul className={styles.ctaList}>
                  {!!primaryCallToAction?.href && (
                    <li>
                      <TextCTA iconVariant="inherit" tag="a" {...primaryCallToAction} />
                    </li>
                  )}
                  {!!secondaryCallToAction?.href && (
                    <li>
                      <TextCTA iconVariant="inherit" tag="a" {...secondaryCallToAction} />
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoreCallToAction;
